import client from './client';

export interface Squad {
  id: string;
  name: string;
  description?: string;
  skillLevel?: string;
  ageGroup?: string;
  maxCapacity: number;
  currentCapacity: number;
  location?: string;
  color?: string;
  imageUrl?: string;
  trainingFee?: number;
  status: string;
  schedule?: ScheduleItem[];
  term?: string;
  coachId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  coach?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  members?: SquadMember[];
}

export interface ScheduleItem {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

export interface SquadMember {
  id: string;
  squadId: string;
  playerId: string;
  status: string;
  registeredAt: string;
  approvedAt?: string;
  approvedBy?: string;
  notes?: string;
  player: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    dateOfBirth?: string;
  };
}

export interface CreateSquadData {
  name: string;
  description?: string;
  skillLevel?: string;
  ageGroup?: string;
  maxCapacity?: number;
  location?: string;
  color?: string;
  imageUrl?: string;
  trainingFee?: number;
  schedule?: ScheduleItem[];
  term?: string;
  coachId?: string;
}

export interface UpdateSquadData {
  name?: string;
  description?: string;
  skillLevel?: string;
  ageGroup?: string;
  maxCapacity?: number;
  location?: string;
  color?: string;
  imageUrl?: string;
  trainingFee?: number;
  status?: string;
  schedule?: ScheduleItem[];
  term?: string;
  coachId?: string;
  isActive?: boolean;
}

export interface SquadsFilters {
  page?: number;
  limit?: number;
  search?: string;
  skillLevel?: string;
  ageGroup?: string;
  status?: string;
  coachId?: string;
}

export const getSquads = async (filters?: SquadsFilters) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
  }

  const response = await client.get(`/squads?${params.toString()}`);
  return response.data;
};

export const getSquadById = async (id: string) => {
  const response = await client.get(`/squads/${id}`);
  return response.data;
};

export const createSquad = async (data: CreateSquadData) => {
  const response = await client.post('/squads', data);
  return response.data;
};

export const updateSquad = async (id: string, data: UpdateSquadData) => {
  const response = await client.put(`/squads/${id}`, data);
  return response.data;
};

export const archiveSquad = async (id: string) => {
  const response = await client.post(`/squads/${id}/archive`);
  return response.data;
};

export const unarchiveSquad = async (id: string) => {
  const response = await client.post(`/squads/${id}/unarchive`);
  return response.data;
};

export const deleteSquad = async (id: string) => {
  const response = await client.delete(`/squads/${id}`);
  return response.data;
};

export const getSquadMembers = async (id: string, status?: string) => {
  const params = status ? `?status=${status}` : '';
  const response = await client.get(`/squads/${id}/members${params}`);
  return response.data;
};

export const registerPlayerToSquad = async (squadId: string, playerId: string, notes?: string) => {
  const response = await client.post(`/squads/${squadId}/register`, {
    playerId,
    notes,
  });
  return response.data;
};

export const approvePlayerRegistration = async (squadId: string, playerId: string) => {
  const response = await client.post(`/squads/${squadId}/members/${playerId}/approve`);
  return response.data;
};

export const removePlayerFromSquad = async (squadId: string, playerId: string) => {
  const response = await client.delete(`/squads/${squadId}/members/${playerId}`);
  return response.data;
};

// Export as object for convenience
export const squadAPI = {
  getSquads,
  getSquadById,
  createSquad,
  updateSquad,
  archiveSquad,
  unarchiveSquad,
  deleteSquad,
  getSquadMembers,
  registerPlayerToSquad,
  approvePlayerRegistration,
  removePlayerFromSquad,
};
